import { Clock } from "lucide-react";
import * as React from "react";
import { inputChassis } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./time-picker.i18n";

const pad = (n: number) => n.toString().padStart(2, "0");

function buildPresets(minuteStep: number): string[] {
  const presets: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += minuteStep) {
      presets.push(`${pad(h)}:${pad(m)}`);
    }
  }
  return presets;
}

function findClosestPresetIndex(value: string, presets: string[]): number {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return -1;
  const [h, m] = value.split(":").map(Number);
  if (h === undefined || m === undefined) return -1;
  const target = h * 60 + m;
  let bestIndex = 0;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (let i = 0; i < presets.length; i++) {
    const [ph, pm] = presets[i].split(":").map(Number);
    const diff = Math.abs(ph * 60 + pm - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = i;
    }
  }
  return bestIndex;
}

export type TimePickerProps = {
  value?: string | null;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  /** Step in minutes for the preset list. Defaults to 30. */
  minuteStep?: number;
  /** Accessible name for the time input when no visible label points at it. */
  "aria-label"?: string;
  "aria-describedby"?: string;
};

export function TimePicker({
  value,
  onChange,
  id,
  name,
  placeholder,
  disabled = false,
  className,
  required,
  minuteStep = 30,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
}: TimePickerProps) {
  const t = useT(T);
  const [open, setOpen] = React.useState(false);
  const presets = React.useMemo(() => buildPresets(minuteStep), [minuteStep]);
  const listRef = React.useRef<HTMLDivElement>(null);

  const handleSelect = (preset: string) => {
    onChange(preset);
    setOpen(false);
  };

  // When opened, scroll the closest matching preset into view.
  React.useEffect(() => {
    if (!open) return;
    const idx = findClosestPresetIndex(value ?? "", presets);
    if (idx < 0) return;
    // Defer so PopoverContent has mounted and laid out.
    const raf = requestAnimationFrame(() => {
      const el = listRef.current?.querySelector<HTMLButtonElement>(`[data-preset-index="${idx}"]`);
      el?.scrollIntoView({ block: "center" });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, value, presets]);

  return (
    <div className={cn("relative flex w-full items-center", className)}>
      <input
        id={id}
        name={name}
        type="time"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        className={cn(
          inputChassis,
          "pl-8 tabular-nums",
          "[&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
        )}
      />
      <Popover open={open} onOpenChange={setOpen}>
        {/* Same handle as the date field: one leading 16px mark, no trailing
            chevron. The preset list accelerates, it never gates entry. */}
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={t.open}
            aria-haspopup="dialog"
            aria-expanded={open}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            <Clock className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[120px] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div ref={listRef} className="max-h-[240px] overflow-y-auto p-1">
            {presets.map((preset, i) => {
              const isSelected = preset === value;
              return (
                <button
                  key={preset}
                  type="button"
                  data-preset-index={i}
                  onClick={() => handleSelect(preset)}
                  className={cn(
                    "flex h-8 w-full items-center rounded-sm px-3 text-left text-sm tabular-nums hover:bg-accent hover:text-accent-foreground",
                    isSelected && "bg-accent font-medium text-accent-foreground",
                  )}
                >
                  {preset}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
